/* The roster's one rule: who needs the coach today.
 *
 * `docs/EVERFIT-MAP.md` §11 timed eight coach jobs in the competitor's product. The asymmetry is
 * the whole product argument: jobs 2, 6 and 8 cost them 20, 3 and unbounded steps and cost us
 * ZERO, because the engine does them — but jobs 1, 4, 5 and 7 cost them 0–3 steps and are
 * UNBUILT for us. Those are the coach's *awareness* jobs, and **a coach's day is mostly jobs 4
 * and 7**:
 *
 *   job 4 — see who needs attention today, across the whole roster, without opening anyone
 *   job 7 — notice someone has quietly stopped training
 *
 * This file is that answer. It is PURE on purpose — no Supabase, no React — so the rule can be
 * executed rather than reasoned about. `roster.test.ts` runs it.
 *
 * 🔴 CORRECTED 2026-08-28, B-4 SESSION 2 — THIS FILE SHIPPED A SENTENCE THAT WAS FALSE ABOUT A
 * REAL PERSON. It read "trained" as `workout_sessions.completed_at`, and therefore told the
 * coach that his client **"Has never trained — 4 workouts assigned, none started."** She had
 * logged **22 sets on 2026-08-11 and 22 more on 2026-08-15**. `completed_at` is written only by
 * the Finish button, so it means *pressed Finish*, not *trained*; `status='completed'` is a
 * third answer again (52 rows against 7). The signal is **a logged set** and the full
 * measurement is in `client.ts`'s header.
 *
 * ⚠️ AND THE ERROR RAN IN THE DIRECTION THAT MATTERS MOST HERE. It did not hide her — it put her
 * at the top of the list under the wrong sentence, so the coach would open the conversation with
 * *"why have you not started?"* to somebody who had started twice and stopped. That is job 7
 * ("notice someone has quietly stopped training") mislabelled as job 1.
 *
 * ⚠️ IT RANKS BY CATEGORY, NOT BY A SCORE, AND THAT IS DELIBERATE — B-2's digest precedent.
 * A weighting fitted to two clients would be an invention, and a single "attention score" hides
 * the thing the coach actually needs, which is WHAT TO DO. Each category below names a different
 * action: chase an invitation, assign some training, ask why nobody has started, or check in on
 * someone who has gone quiet. A number cannot say which.
 */

// ⚠️ `daysSince` USED TO LIVE IN THIS FILE. The client page asks the identical question, so it
// moved to `days.ts` rather than being copied — a second copy of a date rule is this repo's
// defining bug class with a calendar in the place of a vocabulary.
import { daysSince } from "./days";

/** What the screen needs about one person. Assembled from `coach_clients` + `profiles`. */
export type RosterRow = {
  linkId: string;
  clientId: string | null;
  /** `active` | `pending` | … — `coach_clients.status`, never a preference. */
  status: string;
  firstName: string | null;
  lastName: string | null;
  /** Assignments this coach has given them, ever. */
  assignmentCount: number;
  /**
   * Training over the last 30 days as COMPLETED / ASSIGNED — their `Last 30d Training` column,
   * which prints `0%` and cannot say whether the athlete did none of four or was given none.
   * Derived by `clientStatus.trainingCount`; zero of zero is a real and different answer.
   */
  last30: { completed: number; assigned: number };
  /**
   * Their `Last 7d Tasks` column, and it is REAL — a task is a `coach_workouts` row with
   * `kind = 'task'`, assigned through the same RPC into the same `coach_assignments`.
   *
   * ⚠️ IT IS COUNTED APART FROM TRAINING ON PURPOSE. A daily wrist series is an assignment and is
   * not a workout; folding the two makes a diligent athlete look non-compliant, which is the
   * direction that costs a real conversation.
   */
  last7Tasks: { completed: number; assigned: number };
  /**
   * The most recent day they LOGGED A SET, as `YYYY-MM-DD`, or null if none inside the lookback.
   *
   * ⚠️ NULL MEANS "NOTHING IN THE WINDOW", NEVER "NEVER". The query is bounded to
   * `rosterLookbackDays` so a roster does not grow with the coach's whole history, and every
   * label below is worded to survive that bound.
   */
  lastTrainedISO: string | null;
  /**
   * Their `Last 7d Training` column — the same shape as `last30`, over seven days.
   *
   * ⚠️ IT IS A SECOND COLUMN AND NOT A NARROWER READING OF THE SAME ONE. A coach scanning for
   * "who has gone quiet THIS week" and a coach asking "is this holding up over a month" are two
   * questions, which is why theirs prints both and why ours now does.
   */
  last7: { completed: number; assigned: number };
  /**
   * The SOONEST day this client has something assigned that is today or later, as `YYYY-MM-DD`,
   * or null when nothing is scheduled ahead — the input to their `Last Assigned Workout` filter.
   *
   * 🔴 NULL IS "NOTHING SCHEDULED", WHICH IS NOT A DISTANCE, and `matchesAssigned` therefore
   * matches it against no comparator at all. The question it would answer already has a better
   * home on this screen: the rail's own `Need Programming`.
   *
   * ⚠️ AND IT IS THE SOONEST FUTURE DAY, NEVER THE LATEST. "When does their next session fall"
   * is the coach's question; the furthest-out assignment answers "how far ahead have I written",
   * which is a different one and would make a well-programmed client look imminent.
   */
  nextAssignedISO: string | null;
  /* W19(e) — their `Last 30d Tasks`, the same shape as `last7Tasks` over the longer window. It
     costs NO query: the task days are already separated from the training days for `last7Tasks`,
     and the set-log read is bounded at `rosterLookbackDays`, which is wider than 30. */
  last30Tasks: { completed: number; assigned: number };
  /**
   * W19(e) — their `Sign up Date` and `Invite Date`, as `YYYY-MM-DD` or null.
   *
   * ⚠️ THEY ARE TWO DIFFERENT DATES AND THE ROSTER IS THE ONE SCREEN THAT CAN SHOW BOTH.
   * `invitedISO` is `coach_clients.created_at` — the day THIS coach wrote the invitation, which
   * exists for every row including the ones with no account behind them. `signedUpISO` is
   * `profiles.created_at` — the day the person made an account, which is null for a pending row
   * and is NOT this coach's business date: somebody may have signed up years before this coach
   * invited them. A row where the two are far apart is a client who came from somewhere else.
   * ⚠️ AND NEITHER IS "WHEN DID THEY ACCEPT" — that is `accepted_at`, which this query does not
   * read, and inferring it from either of these would be a third date wearing a second one's name.
   */
  signedUpISO: string | null;
  invitedISO: string | null;
  /** N2g — `coach_clients.ended_at` as `YYYY-MM-DD`; null when nobody ended it. Optional: hand-built rows. */
  endedISO?: string | null;
};

export type Attention =
  | { kind: "invitationPending"; label: string; detail: string }
  | { kind: "noTrainingAssigned"; label: string; detail: string }
  | { kind: "nothingLogged"; label: string; detail: string }
  | { kind: "goneQuiet"; label: string; detail: string; days: number }
  | { kind: "fine"; label: string; detail: string; days: number };

/**
 * The threshold for "gone quiet", in days.
 *
 * ⚠️ STATED AS A CONSTANT WITH ITS REASONING, NOT BURIED IN AN `if`. Ten days is a bit over a
 * week, so it does not fire for somebody who trains three times a week and takes a long weekend,
 * and it does fire before a fortnight of silence. It is a JUDGEMENT and it is the only number
 * here that is — everything else in this file is structural. When there is real beta traffic it
 * should be re-priced against what a lapse actually looks like, rather than defended.
 */
export const goneQuietAfterDays = 10;

/**
 * How far back the roster asks about, in days.
 *
 * ⚠️ IT EXISTS SO THE QUERY IS BOUNDED BY TIME RATHER THAN BY CLIENT COUNT. "When did each of
 * these people last train" has no cheap answer through PostgREST — there is no `distinct on`, so
 * the rows come back and the maximum is taken here. Two clients is 349 logged sets; a hundred
 * clients with a year of training each is not. Ninety days keeps it bounded while being far
 * longer than any silence a coach would still call recent.
 *
 * ⚠️ AND THE BOUND CHANGES WHAT MAY BE SAID. A page that looked at ninety days may report
 * "nothing in ninety days"; it may not report "never". That distinction is the whole reason this
 * constant is exported rather than typed into the query — the label quotes it.
 */
export const rosterLookbackDays = 90;

/**
 * The categories, in the order a coach should read them.
 *
 * The ORDER is the product decision. Someone who never accepted the invitation is not a training
 * problem at all — it is the one case where nothing about their training matters yet, so it is
 * checked first and everything else is skipped. Then: assigned nothing, assigned but never
 * started, started but gone quiet.
 */
export function attention(row: RosterRow, today: Date): Attention {
  if (row.status !== "active") {
    return {
      kind: "invitationPending",
      label: "Invitation not accepted",
      /* ⚠️ SHORTENED 2026-09-19, ON HIS STANDING RULE — *the shortest form that keeps the
         meaning, and never repeat a neighbouring line.* It read *"They have not joined yet, so
         nothing you assign will reach them."* directly under the word `Pending`, which says "they
         have not joined yet" already; the useful half is the CONSEQUENCE. 63 characters → 36.
         ⚠️ And it was also the widest string in the roster: a table cell takes its max-content
         width, so this one sentence was setting the `Status` column to 398 px against an 89 px
         header, for every row on the screen. */
      detail: "Nothing you assign reaches them yet.",
    };
  }

  if (row.assignmentCount === 0) {
    return {
      kind: "noTrainingAssigned",
      label: "No training assigned",
      detail: "They have joined and have nothing to do.",
    };
  }

  if (row.lastTrainedISO === null) {
    return {
      kind: "nothingLogged",
      // "Has never trained" is the sentence this file got wrong, and it was wrong twice over:
      // it claimed knowledge of all time from a bounded query, and it claimed nothing had been
      // STARTED from a signal that only ever meant "finished". Both halves are gone.
      label: `Nothing logged in ${rosterLookbackDays} days`,
      detail: `${row.assignmentCount} workout${row.assignmentCount === 1 ? "" : "s"} assigned by you.`,
    };
  }

  const days = daysSince(row.lastTrainedISO, today);
  if (days === null) {
    // An unparseable date is reported, never scored. Treating it as "trained today" would be an
    // invention, and treating it as "never trained" would accuse somebody of quitting because a
    // string was malformed.
    return {
      kind: "nothingLogged",
      label: "Last session unreadable",
      detail: "Their most recent session has a date this screen cannot read.",
    };
  }

  if (days >= goneQuietAfterDays) {
    return {
      kind: "goneQuiet",
      label: `Quiet for ${days} days`,
      detail: "No completed session since then.",
      days,
    };
  }

  return {
    kind: "fine",
    label: days === 0 ? "Trained today" : days === 1 ? "Trained yesterday" : `Trained ${days} days ago`,
    detail: "",
    days,
  };
}

/** Whether a row belongs in the "needs me today" filter. */
export function needsAttention(row: RosterRow, today: Date): boolean {
  return attention(row, today).kind !== "fine";
}

/**
 * A person's name, with the fallback the DATA forces.
 *
 * ⚠️ A ROSTER ROW MAY HAVE NO PROFILE AT ALL. `coach_clients` carries `invited_first_name` /
 * `invited_last_name` / `invited_email` precisely because a row exists from the moment a coach
 * invites somebody — before any account does. `profiles` has no `email` column, so there is no
 * address to fall back to from that side. A screen that reads `profiles.first_name` and stops
 * renders every pending invitation as a blank row.
 */
/* ⚠️ IT TAKES THE TWO FIELDS IT READS, NOT A WHOLE ROW (narrowed 2026-09-18). It used to ask for
 * a `RosterRow`, so adding `last30` to that type broke THREE call sites that only ever wanted a
 * name — the client page, the messages list and two test fixtures had to invent a training
 * counter to print somebody's name. That is the grown-struct trap in a type system: a parameter
 * wider than the function's appetite makes every future field a breaking change for callers that
 * do not care. `Pick` keeps `RosterRow` passable unchanged. */
export function displayName(row: Pick<RosterRow, "firstName" | "lastName">, invited: { firstName: string | null; lastName: string | null; email: string | null }): string {
  const joined = [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
  if (joined !== "") return joined;
  const invitedName = [invited.firstName, invited.lastName].filter(Boolean).join(" ").trim();
  if (invitedName !== "") return invitedName;
  if (invited.email !== null && invited.email !== "") return invited.email;
  return "Unnamed client";
}

/**
 * The roster, ordered so the top of the list is the work.
 *
 * Category first, then longest-quiet first inside it. Sorting by name would be tidy and would
 * bury the one person who needs something under somebody whose surname starts with A.
 */
const categoryOrder: Attention["kind"][] = [
  "invitationPending",
  "noTrainingAssigned",
  "nothingLogged",
  "goneQuiet",
  "fine",
];

export function ordered(rows: RosterRow[], today: Date): RosterRow[] {
  return [...rows].sort((a, b) => {
    const aa = attention(a, today);
    const bb = attention(b, today);
    const byCategory = categoryOrder.indexOf(aa.kind) - categoryOrder.indexOf(bb.kind);
    if (byCategory !== 0) return byCategory;
    const aDays = "days" in aa ? aa.days : 0;
    const bDays = "days" in bb ? bb.days : 0;
    return bDays - aDays;
  });
}


/* ── WHAT COUNTS AS TRAINING, AND WHAT IS A TASK ───────────────────────────────────────────────
 *
 * 🔴 THIS RULE EXISTS BECAUSE ITS ABSENCE SHIPPED A WRONG NUMBER (2026-09-18). A task is a
 * `coach_workouts` row with `kind = 'task'`, assigned through the same RPC into the same
 * `coach_assignments` as a workout. A counter built from "every assignment" therefore counted his
 * DAILY WRIST SERIES as prescribed training — `19 / 23` where the real figure is `19 / 21` — and
 * the error ran in the direction that makes a diligent athlete look non-compliant.
 *
 * ⚠️ AND THE EMBED ARRIVES IN TWO SHAPES. PostgREST returns an embedded row as an OBJECT or as an
 * ARRAY depending on the relationship it infers, which is this repo's own trap; reading `.kind`
 * off the wrong one is `undefined`, and `undefined !== "task"` means every task would silently be
 * counted as training again. Both shapes are handled HERE, once, rather than at each call site.
 *
 * ⚠️ AN UNKNOWN KIND IS TRAINING, DELIBERATELY. `kind` is a CHECK of `workout | task`, so there is
 * no third value today — but if one arrives, counting it as training over-states the denominator
 * (the athlete looks slightly worse) while counting it as a task under-states it (a real workout
 * silently stops being counted at all). Of the two wrong answers, the one that cannot hide work is
 * the safer default, and this comment is what stops it being changed without that argument.
 */
export type AssignmentEmbed =
  | { name?: string | null; kind?: string | null; task_optional?: boolean | null }
  | { name?: string | null; kind?: string | null; task_optional?: boolean | null }[]
  | null
  | undefined;

export function embeddedWorkout(embed: AssignmentEmbed): { name: string | null; kind: string | null; optional: boolean } {
  const row = Array.isArray(embed) ? embed[0] : embed;
  // `task_optional` absent (a select that did not ask) or null reads FALSE — an owed task, which
  // is what every task was before 2026-10-08; the column itself is NOT NULL DEFAULT false.
  return { name: row?.name ?? null, kind: row?.kind ?? null, optional: row?.task_optional === true };
}

export function isTaskAssignment(embed: AssignmentEmbed): boolean {
  return embeddedWorkout(embed).kind === "task";
}

/**
 * 🔴 AN OPTIONAL TASK — offered, never owed (`coach_workouts.task_optional`, his 2026-10-07 word:
 * Sunday's menu and the non-Monday weigh-ins). It is shown on its day and kept OUTSIDE every
 * count: the roster's Tasks 7d / 30d, the week cells' task titles, and the calendar's trained
 * tick. ⚠️ ONE predicate here, asked by every reader — the phone's `DailyTaskDay.isOwed` is its
 * twin. Only a TASK can be optional (the CHECK), so this is never true for a workout.
 */
export function isOptionalTask(embed: AssignmentEmbed): boolean {
  const w = embeddedWorkout(embed);
  return w.kind === "task" && w.optional;
}
